// Color utilities shared across components

export const rgbToHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;

const HANDLEBARS_KEY_RE = /^\{\{\s*[^}]+\s*\}\}$/;

/**
 * @description Check if a string looks like a Handlebars key.
 *
 * @param value - Candidate value
 * @returns True if it matches the {{ key }} pattern
 */
export const isHandlebarsKey = (value?: unknown): boolean => {
  if (value == null) {
    return false;
  }
  const trimmed = String(value).trim();
  return Boolean(trimmed && HANDLEBARS_KEY_RE.test(trimmed));
};

/**
 * @description Ensure a value is wrapped in Handlebars braces.
 *
 * @param value - Raw key value
 * @returns Normalized Handlebars key or undefined when empty
 */
export const ensureHandlebarsKey = (value?: unknown): string | undefined => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return undefined;
  }
  if (isHandlebarsKey(trimmed)) {
    return trimmed;
  }
  return `{{ ${trimmed} }}`;
};

/**
 * @description Normalize Brand Boards key values to Handlebars format.
 *
 * @param value - Raw key (with or without braces)
 * @returns Normalized key or undefined if not a brandboards key
 */
export const normalizeBrandboardsKey = (value?: unknown): string | undefined => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return undefined;
  }
  if (isHandlebarsKey(trimmed)) {
    return trimmed;
  }
  if (!trimmed.startsWith("brandboards.")) {
    return undefined;
  }
  return `{{ ${trimmed} }}`;
};

/**
 * @description Strip Handlebars braces from a key.
 *
 * @param value - Handlebars key
 * @returns Inner key string without braces
 */
export const stripHandlebars = (value?: unknown): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.replace(/^\{\{\s*|\s*\}\}$/g, "");
};

const HEX_COLOR_RE = /^#?(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const isHexColor = (value?: unknown): boolean => {
  const raw = String(value ?? "").trim();
  return HEX_COLOR_RE.test(raw);
};

/**
 * @description Normalize a hex value to an uppercase hex string without a leading '#'.
 *
 * @param value - Incoming hex value (may include leading '#').
 * @returns Uppercase hex string without '#'. Returns empty string for null/undefined/empty.
 */
export const toUpperHex = (value?: unknown): string => {
  const raw = String(value ?? "").trim();
  if (isHandlebarsKey(raw)) {
    return raw;
  }
  return raw.replace(/^#/, "").toUpperCase();
};

/**
 * @description Normalize a hex value to an uppercase hex string with a leading '#'.
 *
 * @param value - Incoming hex value (may include leading '#').
 * @returns Uppercase hex string with '#'. Falls back to '#FFFFFF' when empty.
 */
export const toUpperHexWithHash = (value?: unknown): string => {
  const raw = String(value ?? "").trim();
  if (isHandlebarsKey(raw)) {
    return raw;
  }
  const normalized = toUpperHex(raw);
  return normalized ? `#${normalized}` : "#FFFFFF";
};

const saturateRgb = (rgb: { r: number; g: number; b: number }, factor: number) => {
  const { r, g, b } = rgb;
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0,
    s = 0,
    l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max - min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
    }
    h /= 6;
  }
  s = Math.max(0, Math.min(1, s * factor));
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) {
      t += 1;
    }
    if (t > 1) {
      t -= 1;
    }
    if (t < 1 / 6) {
      return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
      return q;
    }
    if (t < 2 / 3) {
      return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
  };
  let r2 = l,
    g2 = l,
    b2 = l;
  if (s !== 0) {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1 / 3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1 / 3);
  }
  return { r: Math.round(r2 * 255), g: Math.round(g2 * 255), b: Math.round(b2 * 255) };
};

const getColorFromPixels = (
  pixels: number[][],
  quality: number
): { r: number; g: number; b: number } => {
  const sampled = pixels.filter((_, i) => i % quality === 0);
  if (!sampled.length) {
    return { r: 255, g: 255, b: 255 };
  }
  const colorMap = new Map<string, { c: number; r: number; g: number; b: number }>();
  for (const [r, g, b] of sampled) {
    const key = `${(r >> 3) << 3}-${(g >> 3) << 3}-${(b >> 3) << 3}`;
    const entry = colorMap.get(key);
    if (entry) {
      entry.c++;
      entry.r = Math.round((entry.r * (entry.c - 1) + r) / entry.c);
      entry.g = Math.round((entry.g * (entry.c - 1) + g) / entry.c);
      entry.b = Math.round((entry.b * (entry.c - 1) + b) / entry.c);
    } else {
      colorMap.set(key, { c: 1, r, g, b });
    }
  }
  let maxC = 0;
  let dom = { r: 255, g: 255, b: 255 };
  colorMap.forEach((v) => {
    if (v.c > maxC) {
      maxC = v.c;
      dom = { r: v.r, g: v.g, b: v.b };
    }
  });
  return dom;
};

export const computeDominantColorFromImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const w = (canvas.width = 64);
        const h = (canvas.height = 64);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return reject("noctx");
        }
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        const pixels: number[][] = [];
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const alpha = data[i + 3];
          if (alpha < 125) {
            continue;
          }
          const brightness = (r * 299 + g * 587 + b * 114) / 1000;
          if (brightness > 250 || brightness < 5) {
            continue;
          }
          pixels.push([r, g, b]);
        }
        if (pixels.length === 0) {
          resolve("#ffffff");
          return;
        }
        const dominantColor = getColorFromPixels(pixels, 1);
        const saturated = saturateRgb(dominantColor, 1.2);
        resolve(rgbToHex(saturated.r, saturated.g, saturated.b));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = url;
  });
};
